import React, { useEffect, useState, useCallback } from 'react';
import QRCode from 'qrcode';
import { X, Download, Printer, QrCode } from 'lucide-react';
import type { CaseSummary } from '../../../types/case.types';
import { buildQrContent, getSystemCaseNumber, openQrPrintWindow } from '../../../utils/qrCodePrint';

interface QRCodeModalProps {
  caseItem: CaseSummary;
  onClose: () => void;
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({ caseItem, onClose }) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const qrContent = buildQrContent(caseItem.case_id);
  const systemCaseNumber = getSystemCaseNumber(caseItem);
  const displayNumber = caseItem.display_number || '';

  const generateQR = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = await QRCode.toDataURL(qrContent, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 280,
        color: {
          dark: '#1a1a2e',
          light: '#ffffff',
        },
      });
      setQrDataUrl(url);
    } catch (err) {
      setError('Failed to generate QR code.');
    } finally {
      setLoading(false);
    }
  }, [qrContent]);

  useEffect(() => {
    generateQR();
  }, [generateQR]);

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `QR_${displayNumber || systemCaseNumber}.png`;
    link.click();
  };

  const handlePrint = () => {
    if (!qrDataUrl) return;
    openQrPrintWindow(qrDataUrl, caseItem);
  };

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 'var(--radius-xl, 12px)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          width: '100%',
          maxWidth: 420,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <QrCode size={18} color="var(--color-primary)" />
            <span style={{ fontWeight: 700, fontSize: 'var(--font-size-base)', color: 'var(--color-gray-900)' }}>
              Case QR Code
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-gray-500)',
              display: 'flex',
              alignItems: 'center',
            }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* QR Code */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '24px 20px 16px',
            background: 'var(--color-gray-50, #f9fafb)',
          }}
        >
          {loading && (
            <div
              style={{
                width: 280,
                height: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-gray-400)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Generating…
            </div>
          )}
          {error && (
            <div
              style={{
                width: 280,
                height: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-error)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}
          {!loading && !error && qrDataUrl && (
            <>
              <img
                src={qrDataUrl}
                alt={`QR Code for case ${displayNumber || systemCaseNumber}`}
                style={{
                  width: 280,
                  height: 280,
                  borderRadius: 8,
                  border: '1px solid var(--color-gray-200)',
                }}
              />
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                {displayNumber ? (
                  <>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xl, 22px)',
                        color: 'var(--color-gray-900)',
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                      }}
                    >
                      {displayNumber}
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-gray-500)',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Ref: {systemCaseNumber}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-gray-500)',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      System Case Number
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-lg, 18px)',
                        color: 'var(--color-gray-900)',
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {systemCaseNumber}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer — Download / Print buttons */}
        <div
          style={{
            padding: '12px 20px 16px',
            borderTop: '1px solid var(--color-gray-100)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={handlePrint}
            disabled={!qrDataUrl || loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 18px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-gray-300)',
              background: '#fff',
              color: qrDataUrl && !loading ? 'var(--color-gray-800)' : 'var(--color-gray-400)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: qrDataUrl && !loading ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s',
            }}
          >
            <Printer size={15} />
            Print QR Code
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!qrDataUrl || loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 18px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: qrDataUrl && !loading ? 'var(--color-primary)' : 'var(--color-gray-300)',
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: qrDataUrl && !loading ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s',
            }}
          >
            <Download size={15} />
            Download QR Code
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;
